// ✅ src/pages/VehicleV2/VehicleSalesScrapListing.jsx  (UPDATED FULL FILE)
// Mount backend:
//   app.use("/api/salesandScrapListing", salesandScrapListing);
//
// Backend endpoints used:
//   GET  /api/salesandScrapListing/dropdowns?branchId=&branchType=&loginUserId=
//   GET  /api/salesandScrapListing/list?branchId=&branchType=&loginUserId=&salesScrap=&vehicleCode=&store=&vehicleName=&vehicleNumber=&vehicleMainType=
//   POST /api/salesandScrapListing/delete   { ids:[...] }
//
// ✅ Updated pattern:
// - common localStorage / sessionStorage header pattern
// - useMemo + useCallback pattern
// - pageLoading + dropdownLoading + gridLoading + actionLoading states
// - safer init / refresh / filter / list flow
// - fixed stale-state issue in View All
// - avoids unnecessary repeated API calls
// - added client-side pagination
// - safer select all / delete selected flow

import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FaPlus,
  FaSearch,
  FaFilter,
  FaTrash,
  FaEye,
  FaTimes,
  FaEdit,
  FaArrowLeft,
  FaSyncAlt,
  FaUndo,
} from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/salesandScrapListing";

/* ---------------- helpers ---------------- */
const toStr = (v, def = "") => {
  if (v === null || v === undefined) return def;
  return String(v).trim();
};

const toInt = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : d;
};

const toList = (arr) => (Array.isArray(arr) ? arr : []);

const clean = (v) => String(v ?? "").trim();

const selectValue = (v, def = "Select") => {
  const s = clean(v);
  return s ? s : def;
};

const defaultFilters = {
  salesScrap: "All",
  vehicleCode: "",
  store: "Select",
  vehicleName: "",
  vehicleNumber: "",
  vehicleMainType: "Select",
  search: "",
};

const defaultSaleTypes = [
  { label: "All", value: "All" },
  { label: "Sales", value: "Sales" },
  { label: "Scrap", value: "Scrap" },
];

const VehicleSalesScrapListing = () => {
  const navigate = useNavigate();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  /* ---------------- shared local/session pattern ---------------- */
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId =
    localStorage.getItem("branchId") ||
    localStorage.getItem("Branchid") ||
    localStorage.getItem("branchid") ||
    "8";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "0";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "0";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  /* ---------------- state ---------------- */
  const [showFilters, setShowFilters] = useState(true);

  const [pageLoading, setPageLoading] = useState(false);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [gridLoading, setGridLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const [filters, setFilters] = useState(defaultFilters);

  const [opts, setOpts] = useState({
    stores: [{ label: "Select", value: "Select" }],
    vehicleMainTypes: [{ label: "Select", value: "Select" }],
    saleTypes: defaultSaleTypes,
  });

  const [rows, setRows] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);

  const [page, setPage] = useState(1);
  const pageSize = 10;

  const isBusy = pageLoading || dropdownLoading || gridLoading || actionLoading;

  /* ---------------- styles ---------------- */
  const cardClass = useMemo(
    () =>
      `p-6 mt-6 rounded-2xl shadow-xl border ${
        isDark
          ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
          : `bg-white ${currentSkin.glass} text-black`
      }`,
    [isDark, currentSkin]
  );

  const sectionClass = useMemo(
    () =>
      `rounded-xl p-6 border ${
        isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
      }`,
    [isDark]
  );

  const inputClass = useMemo(
    () =>
      `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
        isDark
          ? "bg-gray-700/30 border-gray-600 text-white placeholder:text-gray-400"
          : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () =>
      `block text-sm font-medium mb-2 ${
        isDark ? "text-gray-300" : "text-gray-700"
      }`,
    [isDark]
  );

  const pillClass = useMemo(
    () =>
      `inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
        isDark ? "bg-blue-900/60 text-blue-100" : "bg-blue-100 text-blue-800"
      }`,
    [isDark]
  );

  /* ---------------- helpers ---------------- */
  const canUse = useCallback((cond, msg) => {
    if (!cond) toast.error(msg);
    return !!cond;
  }, []);

  const setField = useCallback((name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  }, []);

  const resetFiltersOnly = useCallback(() => {
    setFilters(defaultFilters);
    setPage(1);
    setSelectedIds([]);
  }, []);

  const buildListParams = useCallback(
    (override = {}) => ({
      branchId,
      branchType,
      loginUserId,
      salesScrap: selectValue(
        override.salesScrap ?? filters.salesScrap,
        "All"
      ),
      vehicleCode: toStr(override.vehicleCode ?? filters.vehicleCode),
      vehicleName: toStr(override.vehicleName ?? filters.vehicleName),
      vehicleNumber: toStr(override.vehicleNumber ?? filters.vehicleNumber),
      vehicleMainType: selectValue(
        override.vehicleMainType ?? filters.vehicleMainType,
        "Select"
      ),
      store: selectValue(override.store ?? filters.store, "Select"),
    }),
    [branchId, branchType, loginUserId, filters]
  );

  /* =========================================================
     mapped rows + quick search + pagination
     ========================================================= */
  const mappedRows = useMemo(() => {
    const q = toStr(filters.search).toLowerCase();

    const list = toList(rows).map((r, idx) => {
      const id =
        r.id ||
        r.vehicleId ||
        r.fld_id ||
        r.fld_vehicleid ||
        r.salesScrapId ||
        "";

      const branch =
        r.branch ||
        r.fld_BranchName ||
        r.fld_branchname ||
        r.branchName ||
        "";

      const vehicleMainType =
        r.vehicleMainType ||
        r.VehicleType ||
        r.fld_vehiclemaintype ||
        r.fld_vehicletypename ||
        r.vehicletype ||
        "";

      const vehicleCode =
        r.vehicleCode ||
        r.fld_vehicle_code ||
        r.fld_vehiclecode ||
        "";

      const vehicleName =
        r.vehicleName ||
        r.fld_vehicle_name ||
        r.fld_vehiclename ||
        "";

      const vehicleNumber =
        r.vehicleNumber ||
        r.fld_vehicle_regno ||
        r.fld_vehicleno ||
        r.vehicleNo ||
        "";

      const salesScrap =
        r.salesScrap ||
        r.fld_salesscrap ||
        r.saleType ||
        r.type ||
        "";

      return {
        __rowId: idx + 1,
        raw: r,
        id: toInt(id),
        branch,
        vehicleMainType,
        vehicleCode,
        vehicleName,
        vehicleNumber,
        salesScrap: toStr(salesScrap),
      };
    });

    if (!q) return list;

    return list.filter((x) => {
      return (
        toStr(x.branch).toLowerCase().includes(q) ||
        toStr(x.vehicleMainType).toLowerCase().includes(q) ||
        toStr(x.vehicleCode).toLowerCase().includes(q) ||
        toStr(x.vehicleName).toLowerCase().includes(q) ||
        toStr(x.vehicleNumber).toLowerCase().includes(q) ||
        toStr(x.salesScrap).toLowerCase().includes(q)
      );
    });
  }, [rows, filters.search]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(mappedRows.length / pageSize)),
    [mappedRows.length]
  );

  const paginatedRows = useMemo(
    () => mappedRows.slice((page - 1) * pageSize, page * pageSize),
    [mappedRows, page]
  );

  const recordsCount = useMemo(() => mappedRows.length, [mappedRows.length]);

  const currentPageIds = useMemo(
    () => paginatedRows.map((r) => toInt(r.id)).filter(Boolean),
    [paginatedRows]
  );

  const selectAll = useMemo(() => {
    if (currentPageIds.length === 0) return false;
    return currentPageIds.every((id) => selectedIds.includes(id));
  }, [currentPageIds, selectedIds]);

  const canDelete = selectedIds.length > 0 && !isBusy;

  /* =========================================================
     API LOADERS
     ========================================================= */
  const loadDropdowns = useCallback(async () => {
    const { data } = await axios.get(`${API_BASE}/dropdowns`, {
      params: {
        branchId,
        branchType,
        loginUserId,
      },
      headers: commonHeaders,
    });

    if (!data?.success) {
      throw new Error(data?.message || "Failed to load dropdowns");
    }

    const stores = toList(data.stores).map((x) => ({
      label: String(x.label ?? x.name ?? "Select"),
      value: String(x.value ?? x.id ?? "Select"),
    }));

    const vehicleMainTypes = toList(
      data.vehicleMainTypes || data.vehicleTypes
    ).map((x) => ({
      label: String(x.label ?? x.name ?? x.fld_vehicletypename ?? "Select"),
      value: String(
        x.value ?? x.id ?? x.fld_vehicletypeindex ?? "Select"
      ),
    }));

    const saleTypes = toList(data.saleTypes).map((x) => ({
      label: String(x.label ?? x.name ?? x.value ?? "All"),
      value: String(x.value ?? x.id ?? x.label ?? "All"),
    }));

    setOpts({
      stores: stores.length ? stores : [{ label: "Select", value: "Select" }],
      vehicleMainTypes: vehicleMainTypes.length
        ? vehicleMainTypes
        : [{ label: "Select", value: "Select" }],
      saleTypes: saleTypes.length ? saleTypes : defaultSaleTypes,
    });
  }, [branchId, branchType, loginUserId, commonHeaders]);

  const loadList = useCallback(
    async ({ silent = false, overrideFilters = null } = {}) => {
      if (!canUse(toInt(loginUserId), "loginUserId missing")) return;
      if (!canUse(toInt(branchId), "branchId missing")) return;

      try {
        setGridLoading(true);

        const { data } = await axios.get(`${API_BASE}/list`, {
          params: buildListParams(overrideFilters || {}),
          headers: commonHeaders,
        });

        if (!data?.success) {
          setRows([]);
          setPage(1);
          setSelectedIds([]);
          if (!silent) toast.warn(data?.message || "No records found");
          return;
        }

        const listRows = toList(data.list);
        setRows(listRows);
        setPage(1);
        setSelectedIds([]);

        if (!silent) {
          toast.success(`Found ${listRows.length} record(s)`);
        }
      } catch (err) {
        console.error(err);
        setRows([]);
        setPage(1);
        setSelectedIds([]);
        toast.error(
          err?.response?.data?.message || err.message || "Failed to load list"
        );
      } finally {
        setGridLoading(false);
      }
    },
    [loginUserId, branchId, commonHeaders, buildListParams, canUse]
  );

  const loadInit = useCallback(async () => {
    if (!canUse(toInt(loginUserId), "loginUserId missing")) return;
    if (!canUse(toInt(branchId), "branchId missing")) return;

    try {
      setPageLoading(true);
      setDropdownLoading(true);
      await loadDropdowns();
      await loadList({ silent: true });
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err.message || "Initialization failed"
      );
    } finally {
      setDropdownLoading(false);
      setPageLoading(false);
    }
  }, [loginUserId, branchId, canUse, loadDropdowns, loadList]);

  /* ---------------- actions ---------------- */
  const handleSearch = useCallback(async () => {
    await loadList({ silent: false });
  }, [loadList]);

  const handleViewAll = useCallback(async () => {
    const resetObj = { ...defaultFilters };
    setFilters(resetObj);

    await loadList({
      silent: false,
      overrideFilters: resetObj,
    });
  }, [loadList]);

  const handleRefresh = useCallback(async () => {
    await loadList({ silent: false });
  }, [loadList]);

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleSelectAll = useCallback(() => {
    if (selectAll) {
      setSelectedIds((prev) =>
        prev.filter((id) => !currentPageIds.includes(id))
      );
      return;
    }

    setSelectedIds((prev) => {
      const merged = [...prev];
      currentPageIds.forEach((id) => {
        if (!merged.includes(id)) merged.push(id);
      });
      return merged;
    });
  }, [selectAll, currentPageIds]);

  const toggleSelect = useCallback((id) => {
    const n = toInt(id);
    if (!n) return;

    setSelectedIds((prev) =>
      prev.includes(n) ? prev.filter((x) => x !== n) : [...prev, n]
    );
  }, []);

  const deleteSelected = useCallback(async () => {
    if (!selectedIds.length) {
      toast.warn("Please select at least one record!");
      return;
    }

    try {
      setActionLoading(true);

      const { data } = await axios.post(
        `${API_BASE}/delete`,
        { ids: selectedIds },
        { headers: commonHeaders }
      );

      if (!data?.success) {
        throw new Error(data?.message || "Delete failed");
      }

      toast.success(`Deleted ${data.deleted || selectedIds.length} record(s)`);
      await loadList({ silent: true });
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err.message || "Delete failed"
      );
    } finally {
      setActionLoading(false);
    }
  }, [selectedIds, commonHeaders, loadList]);

  const openEdit = useCallback(
    (row) => {
      navigate("/vehicle/Vehicle_SalesandScrapEntry", {
        state: { id: row.id, row: row.raw || row },
      });
    },
    [navigate]
  );

  /* ---------------- effects ---------------- */
  useEffect(() => {
    loadInit();
  }, [loadInit]);

  useEffect(() => {
    setPage((p) => Math.min(p, totalPages));
  }, [totalPages]);

  const renderRow = (row, idx) => {
    const sno = (page - 1) * pageSize + idx + 1;
    const isSelected = selectedIds.includes(toInt(row.id));

    return (
      <tr
        key={`${row.id || row.__rowId || ""}-${sno}`}
        className={isDark ? "border-t border-gray-800" : "border-t border-gray-100"}
      >
        <td className="px-4 py-4 text-sm text-center">{sno}</td>
        <td className="px-4 py-4 text-sm text-center">{row.branch || "-"}</td>
        <td className="px-4 py-4 text-sm text-center">
          {row.vehicleMainType || "-"}
        </td>
        <td className="px-4 py-4 text-sm text-center">
          {row.vehicleCode || "-"}
        </td>
        <td className="px-4 py-4 text-sm text-center">
          {row.vehicleName || "-"}
        </td>
        <td className="px-4 py-4 text-sm text-center">
          {row.vehicleNumber || "-"}
        </td>

        <td className="px-4 py-4 text-center">
          <span
            className={`px-2 py-1 text-xs font-semibold rounded-full ${
              toStr(row.salesScrap).toLowerCase() === "sales"
                ? "bg-green-100 text-green-800"
                : "bg-red-100 text-red-800"
            }`}
          >
            {row.salesScrap || "-"}
          </span>
        </td>

        <td className="px-4 py-4 text-center">
          <button
            type="button"
            onClick={() => openEdit(row)}
            disabled={isBusy}
            className={`inline-flex items-center justify-center px-3 py-2 bg-blue-600 text-white text-sm rounded-lg shadow hover:bg-blue-700 transition ${
              isBusy ? "opacity-60 cursor-not-allowed" : ""
            }`}
            title="Edit"
          >
            <FaEdit />
          </button>
        </td>

        <td className="px-4 py-4 text-center">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => toggleSelect(row.id)}
            disabled={isBusy || !row.id}
          />
        </td>
      </tr>
    );
  };

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">
              VEHICLE SALES AND SCRAP LISTING
            </span>
          }
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={cardClass}>
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <div className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Vehicle Sales and Scrap Listing
              </div>

              <div className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Branch ID: {branchId || "-"} {" • "} User ID: {loginUserId || "-"}
              </div>

              <div className="mt-2 flex flex-wrap gap-2">
                <span className={pillClass}>Records: {recordsCount}</span>
                <span className={pillClass}>Branch Type: {branchType || "-"}</span>
                <span className={pillClass}>Selected: {selectedIds.length}</span>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setShowFilters((s) => !s)}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white"
                    : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                } ${isBusy ? "opacity-70 cursor-not-allowed" : ""}`}
              >
                <FaFilter className="w-4 h-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={handleRefresh}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white"
                    : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                } ${isBusy ? "opacity-70 cursor-not-allowed" : ""}`}
              >
                <FaSyncAlt className="w-4 h-4" />
                Refresh
              </button>

              <button
                onClick={() => navigate("/vehicle/Vehicle_SalesandScrapList")}
                disabled={isBusy}
                className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isBusy ? "opacity-70 cursor-not-allowed" : ""
                }`}
              >
                <FaPlus className="w-4 h-4" />
                Add New
              </button>

              <button
                onClick={handleBack}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white"
                    : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                } ${isBusy ? "opacity-70 cursor-not-allowed" : ""}`}
              >
                <FaArrowLeft className="w-4 h-4" />
                Back
              </button>
            </div>
          </div>

          {/* Filters */}
          {showFilters && (
            <div className={sectionClass}>
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <FaFilter /> FILTRATION AREA
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className={labelClass}>Sales Or Scrap</label>
                  <select
                    name="salesScrap"
                    value={filters.salesScrap}
                    onChange={(e) => setField("salesScrap", e.target.value)}
                    className={inputClass}
                    disabled={isBusy}
                  >
                    {opts.saleTypes.map((o, i) => (
                      <option key={i} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Vehicle Code</label>
                  <input
                    type="text"
                    value={filters.vehicleCode}
                    onChange={(e) => setField("vehicleCode", e.target.value)}
                    className={inputClass}
                    placeholder="Enter vehicle code"
                    disabled={isBusy}
                  />
                </div>

                <div>
                  <label className={labelClass}>Store</label>
                  <select
                    name="store"
                    value={filters.store}
                    onChange={(e) => setField("store", e.target.value)}
                    className={inputClass}
                    disabled={isBusy}
                  >
                    {opts.stores.map((o, i) => (
                      <option key={i} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Vehicle Name</label>
                  <input
                    type="text"
                    value={filters.vehicleName}
                    onChange={(e) => setField("vehicleName", e.target.value)}
                    className={inputClass}
                    placeholder="Enter vehicle name"
                    disabled={isBusy}
                  />
                </div>

                <div>
                  <label className={labelClass}>Vehicle Number</label>
                  <input
                    type="text"
                    value={filters.vehicleNumber}
                    onChange={(e) => setField("vehicleNumber", e.target.value)}
                    className={inputClass}
                    placeholder="Enter vehicle number"
                    disabled={isBusy}
                  />
                </div>

                <div>
                  <label className={labelClass}>Vehicle Main Type</label>
                  <select
                    name="vehicleMainType"
                    value={filters.vehicleMainType}
                    onChange={(e) => setField("vehicleMainType", e.target.value)}
                    className={inputClass}
                    disabled={isBusy}
                  >
                    {opts.vehicleMainTypes.map((o, i) => (
                      <option key={i} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* action buttons */}
              <div className="flex flex-col md:flex-row gap-3 justify-between items-center mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={resetFiltersOnly}
                  disabled={isBusy}
                  className={`flex items-center gap-2 ${
                    isDark
                      ? "text-gray-300 hover:text-white"
                      : "text-gray-600 hover:text-gray-900"
                  } font-medium transition-colors duration-200 ${
                    isBusy ? "opacity-60 cursor-not-allowed" : ""
                  }`}
                >
                  <FaUndo />
                  Clear Filters
                </button>

                <div className="flex flex-col md:flex-row items-center gap-3 w-full md:w-auto">
                  <input
                    value={filters.search}
                    onChange={(e) => setField("search", e.target.value)}
                    className={`${inputClass} md:w-72`}
                    placeholder="Quick search in results..."
                    disabled={isBusy}
                  />

                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={handleSearch}
                      disabled={isBusy}
                      className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-60`}
                    >
                      <FaSearch />
                      {gridLoading ? "Searching..." : "Search"}
                    </button>

                    <button
                      onClick={handleViewAll}
                      disabled={isBusy}
                      className="flex items-center gap-2 px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-lg font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-60"
                    >
                      <FaEye />
                      View All
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* GRID */}
          <div className="mt-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-3">
              <div className={`text-lg font-semibold ${isDark ? "text-white" : "text-gray-800"}`}>
                Vehicle Sales and Scrap List
              </div>
              <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Records: <span className="font-semibold">{mappedRows.length}</span>
              </div>
            </div>

            <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
              <table className="min-w-full text-sm">
                <thead className={`${buttonClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-semibold">S.No</th>
                    <th className="px-4 py-4 text-center font-semibold">Branch</th>
                    <th className="px-4 py-4 text-center font-semibold">Vehicle Main Type</th>
                    <th className="px-4 py-4 text-center font-semibold">Vehicle Code</th>
                    <th className="px-4 py-4 text-center font-semibold">Vehicle Name</th>
                    <th className="px-4 py-4 text-center font-semibold">Vehicle Number</th>
                    <th className="px-4 py-4 text-center font-semibold">Sales/Scrap</th>
                    <th className="px-4 py-4 text-center font-semibold">Edit</th>
                    <th className="px-4 py-4 text-center font-semibold">
                      <div className="flex items-center justify-center gap-2">
                        Select All
                        <input
                          type="checkbox"
                          checked={selectAll}
                          onChange={handleSelectAll}
                          disabled={isBusy || currentPageIds.length === 0}
                        />
                      </div>
                    </th>
                  </tr>
                </thead>

                <tbody className={isDark ? "bg-gray-900 text-gray-100" : "bg-white text-gray-800"}>
                  {mappedRows.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="px-4 py-10 text-center text-gray-500">
                        {gridLoading || pageLoading ? (
                          "Loading..."
                        ) : (
                          <div className="flex flex-col items-center gap-3">
                            <FaTimes className="text-2xl opacity-60" />
                            <span>No records found</span>
                          </div>
                        )}
                      </td>
                    </tr>
                  ) : (
                    paginatedRows.map((row, idx) => renderRow(row, idx))
                  )}
                </tbody>
              </table>
            </div>

            {/* Bottom actions */}
            <div className="flex flex-col md:flex-row md:justify-between gap-3 mt-6">
              <div className="flex gap-3">
                <button
                  onClick={deleteSelected}
                  disabled={!canDelete}
                  className={`flex items-center gap-2 px-4 py-2 text-white text-sm rounded-lg shadow transition ${
                    canDelete
                      ? `${buttonClass} ${hoverClass}`
                      : "bg-gray-400 cursor-not-allowed"
                  }`}
                >
                  <FaTrash /> Delete Selected
                </button>

                <button
                  onClick={handleViewAll}
                  disabled={isBusy}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm rounded-lg shadow hover:bg-green-700 transition disabled:opacity-60"
                >
                  <FaEye /> View All
                </button>
              </div>
            </div>

            {/* Pagination */}
            {mappedRows.length > 0 && (
              <div className="flex justify-center mt-6">
                <nav className="flex items-center space-x-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1 || isBusy}
                    className={`px-3 py-1 border rounded ${
                      isDark
                        ? "bg-gray-800 text-white border-gray-700"
                        : "bg-white text-gray-700 border-gray-300"
                    } disabled:opacity-50`}
                  >
                    Prev
                  </button>

                  {Array.from({ length: totalPages }).map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setPage(i + 1)}
                      disabled={isBusy}
                      className={`px-3 py-1 border rounded ${
                        page === i + 1
                          ? `${buttonClass} text-white`
                          : isDark
                          ? "bg-gray-800 text-white border-gray-700"
                          : "bg-white text-gray-700 border-gray-300"
                      } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                    >
                      {i + 1}
                    </button>
                  ))}

                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages || isBusy}
                    className={`px-3 py-1 border rounded ${
                      isDark
                        ? "bg-gray-800 text-white border-gray-700"
                        : "bg-white text-gray-700 border-gray-300"
                    } disabled:opacity-50`}
                  >
                    Next
                  </button>
                </nav>
              </div>
            )}

            {isBusy && (
              <div className={`mt-3 text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                {pageLoading
                  ? "Loading page..."
                  : dropdownLoading
                  ? "Loading dropdowns..."
                  : gridLoading
                  ? "Loading grid..."
                  : actionLoading
                  ? "Processing action..."
                  : "Loading..."}
              </div>
            )}
          </div>

          <ToastContainer
            position="top-right"
            autoClose={2500}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default VehicleSalesScrapListing;